import { queryClient, reactQueryUtils } from "@lib/react-query";
import { useMutation } from "@tanstack/react-query";
import type { IStoriesFolder } from "@/shared/types";
import {
  foldersApi,
  type ICreateFolderParams,
  type IUpdateFolderParams,
} from "./folders.api";
import { foldersKeys } from "./folders.keys";

export const useCreateFolderMutation = () => {
  return useMutation({
    mutationFn: (data: ICreateFolderParams) => foldersApi.createFolder(data),
    onSuccess: (data: IStoriesFolder) => {
      // Optimistically update the folders list
      queryClient.setQueryData<IStoriesFolder[]>(
        foldersKeys.folders,
        (old: IStoriesFolder[] | undefined) => {
          if (!old) return [data];
          return [...old, data];
        },
      );
      reactQueryUtils.invalidateQueries([...foldersKeys.folders]);
    },
  });
};

export const useUpdateFolderMutation = () => {
  return useMutation({
    mutationFn: (data: IUpdateFolderParams) => foldersApi.updateFolder(data),
    onSuccess: (data: IStoriesFolder, variables: IUpdateFolderParams) => {
      // Optimistically update the folders list
      queryClient.setQueryData<IStoriesFolder[]>(
        foldersKeys.folders,
        (old: IStoriesFolder[] | undefined) => {
          if (!old) return [];
          const idx = old.findIndex(
            (el: IStoriesFolder) => el._id === variables.id,
          );
          if (idx >= 0) {
            const updated = [...old];
            updated[idx] = { ...updated[idx], ...data };
            return updated;
          }
          return old;
        },
      );
      // Invalidate specific folder query
      reactQueryUtils.invalidateQueries([...foldersKeys.folder(variables.id)]);
      reactQueryUtils.invalidateQueries([...foldersKeys.folders]);
    },
  });
};

export const useDeleteFolderMutation = () => {
  return useMutation({
    mutationFn: (id: string) => foldersApi.deleteFolder(id),
    onSuccess: (_data: IStoriesFolder, variables: string) => {
      // Optimistically update the folders list
      queryClient.setQueryData<IStoriesFolder[]>(
        foldersKeys.folders,
        (old: IStoriesFolder[] | undefined) => {
          if (!old) return [];
          return old.filter(
            (folder: IStoriesFolder) => folder._id !== variables,
          );
        },
      );
      reactQueryUtils.invalidateQueries([...foldersKeys.folders]);
    },
  });
};

export const useReorderFoldersMutation = () => {
  return useMutation({
    mutationFn: (folderIds: string[]) => foldersApi.reorderFolders(folderIds),
    onMutate: async (variables) => {
      // Cancel outgoing refetches
      await queryClient.cancelQueries({ queryKey: foldersKeys.folders });

      // Snapshot the previous value
      const previousFolders = queryClient.getQueryData<IStoriesFolder[]>(
        foldersKeys.folders,
      );

      // Optimistically update to the new value
      if (previousFolders) {
        const folderMap = new Map(previousFolders.map((f) => [f._id, f]));
        const reorderedFolders: IStoriesFolder[] = [];

        // Add folders in the order specified by folderIds
        variables.forEach((id, index) => {
          const folder = folderMap.get(id);
          if (folder) {
            folder.order = index;
            reorderedFolders.push(folder);
            folderMap.delete(id);
          }
        });

        // Add remaining folders that weren't in folderIds, preserving their relative order
        const remainingFolders = Array.from(folderMap.values()).sort(
          (a, b) => (a.order ?? 0) - (b.order ?? 0),
        );
        remainingFolders.forEach((folder, index) => {
          folder.order = reorderedFolders.length + index;
          reorderedFolders.push(folder);
        });

        queryClient.setQueryData<IStoriesFolder[]>(
          foldersKeys.folders,
          reorderedFolders,
        );
      }

      return { previousFolders };
    },
    onError: (_err, _variables, context) => {
      // Rollback on error
      if (context?.previousFolders) {
        queryClient.setQueryData(foldersKeys.folders, context.previousFolders);
      }
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: foldersKeys.folders });
    },
  });
};
